import { Checkbox, Button, Popover } from "antd";
import { JEPSON_DATA_ENABLED } from "utils/jepsonVisibility";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

interface Props {
  onChange: any;
  columns: any;
  type?: string; // "plant" or "animal"
}

const CategoryDropdownsWrapper = styled.div`
  .category-pills-container {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
    max-width: 100%;
    justify-content: flex-start;
  }

  .category-pill {
    color: #fff;
    border: none;
    font-weight: var(--font-medium);
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-4);
    height: auto;
    border-radius: 25px;
    box-shadow: var(--shadow-subtle);
    transition: all var(--transition-base);
    white-space: nowrap;

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-medium);
      color: #fff !important;
      opacity: 0.9;
    }

    &:active {
      transform: translateY(0);
    }
  }

  .pill-main-data {
    background: #1d3129;
    &:hover { background: #1d3129 !important; }
  }

  .pill-jepson {
    background: #1d6045;
    &:hover { background: #1d6045 !important; }
  }

  .pill-rankings {
    background: #209d7e;
    &:hover { background: #209d7e !important; }
  }

  .pill-cnps {
    background: #60d8b9;
    &:hover { background: #60d8b9 !important; }
  }

  .pill-cal-ipc {
    background: #a8d5ba;
    &:hover { background: #a8d5ba !important; }
  }

  .pill-cdfw {
    background: #6dc1c4;
    &:hover { background: #6dc1c4 !important; }
  }

  .pill-blm {
    background: #3898b8;
    &:hover { background: #3898b8 !important; }
  }

  .pill-wetland {
    background: #116d8b;
    &:hover { background: #116d8b !important; }
  }

  .pill-ecology {
    background: #60d8b9;
    &:hover { background: #60d8b9 !important; }
  }

  .pill-notes {
    background: #a8d5ba;
    &:hover { background: #a8d5ba !important; }
  }

  .category-popover {
    .ant-popover-inner {
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-strong);
    }
  }

  .category-content {
    min-width: 200px;
    max-height: 400px;
    overflow-y: auto;
    padding: var(--space-2);
  }

  .category-header {
    font-weight: var(--font-semibold);
    font-size: var(--text-base);
    margin-bottom: var(--space-3);
    padding-bottom: var(--space-2);
    border-bottom: 1px solid var(--color-divider);
  }

  .checkbox-item {
    display: block;
    margin-bottom: var(--space-2);
    padding: var(--space-1);

    &:hover {
      background: var(--color-hover);
      border-radius: var(--radius-sm);
    }
  }

  @media (max-width: 768px) {
    .category-pills-container {
      justify-content: center;
    }

    .category-pill {
      flex: 1 1 calc(50% - var(--space-3));
      min-width: 120px;
    }
  }
`;

// Field-to-category mapping for PLANTS
const PLANT_CATEGORY_MAPPING = {
  // Main Data
  "full_name": "Main Data",
  "family": "Main Data",
  "family_name": "Main Data",
  "common_name_1": "Main Data",
  "common_name_2": "Main Data",
  "common_name_3": "Main Data",
  "common_name_4": "Main Data",
  "common_name_5": "Main Data",
  "plant_type": "Main Data",
  "monocot_dicot": "Main Data",
  "native": "Main Data",
  "elevation_range": "Main Data",
  "ai_description": "Main Data",
  "ai_differentiators": "Main Data",
  "col1_species_differentiator": "Main Data",
  "col1_key_characters": "Main Data",
  "col2_genus_confidence_check": "Main Data",
  "col2_quick_tells": "Main Data",
  "family_differentiator": "Main Data",
  "family_key_characters": "Main Data",
  "chez_elevation": "Main Data",
  "chez_bloom_window": "Main Data",
  "chez_range": "Main Data",
  "chez_plant_type": "Main Data",
  "synonyms_1": "Main Data",
  "synonyms_2": "Main Data",
  "synonyms_3": "Main Data",
  "synonyms_4": "Main Data",
  "note": "Main Data",

  // De-labelled 2026-09-22. These were grouped under "Jepson eFlora"; the data is
  // plain fact (a bloom month, an elevation band, a photo or reference link) and
  // stays, but it is no longer presented as Jepson's. The Jepson-sourced entries
  // that used to sit here — jepson_description, jepson_eflora_other_notes and
  // bioregional_distributions — are added back only when the flag is on, below.
  "habitat": "Ecology",
  // elevation_range is already grouped under Main Data above.
  "flowering_time": "Ecology",
  "distribution_outside_of_california": "Ecology",
  "cal_photo_link": "Main Data",
  "url": "Main Data",
  "taxonomy_notes": "Main Data",

  // Rankings
  "california_rare_plant_rank": "Rankings",
  "global_rank": "Rankings",
  "federal_listing_status": "Rankings",
  "state_listing_status": "Rankings",
  "forest_service_status": "Rankings",
  "CNPSRareLink": "Rankings",

  // CNPS Data
  "cnps_notes": "CNPS Data",
  "cnps_taxonomy": "CNPS Data",
  "cnps_threats": "CNPS Data",
  "cnps_name": "CNPS Data",

  // Cal IPC Data
  "ipc_listed_weed": "CAL IPC",
  "calipc_rating": "CAL IPC",
  "calipc_description": "CAL IPC",
  "calipc_name": "CAL IPC",
  "calipc_genus": "CAL IPC",
  "calipc_species": "CAL IPC",
  "calipc_variety": "CAL IPC",
  "cdfa_rating": "CAL IPC",

  // CDFW Data
  "cdfw_genus": "CDFW Data",
  "cdfw_species": "CDFW Data",
  "cdfw_variety": "CDFW Data",
  "cdfw_element_code": "CDFW Data",

  // BLM Data
  "blm_recognized_name": "BLM Data",
  "known_to_occur": "BLM Data",
  "suspected_to_occur": "BLM Data",
  "nnps": "BLM Data",
  "nvstate_rank": "BLM Data",
  "blm_family": "BLM Data",
  "blm_sensitive": "BLM Data",

  // Wetland Status
  "usace_wetland_name": "Wetland",
  "usace_wetland_status": "Wetland",
  "usda_common_name": "Wetland",
  "usda_symbol": "Wetland",
  "usda_arid_west_status": "Wetland",
  "usda_wmvc_status": "Wetland",
};

// Field-to-category mapping for ANIMALS
const ANIMAL_CATEGORY_MAPPING = {
  // Taxonomy & Identification
  "phylum": "Taxonomy",
  "database": "Taxonomy",
  "family_name": "Taxonomy",
  "genus": "Taxonomy",
  "species": "Taxonomy",
  "sub_species": "Taxonomy",
  "sub_family": "Taxonomy",
  "variety": "Taxonomy",
  "scientific_names": "Taxonomy",
  "common_names": "Taxonomy",
  "blm_recognized_name": "Taxonomy",
  "synonym_1": "Taxonomy",
  "synonym_2": "Taxonomy",
  "synonym_3": "Taxonomy",
  "animal_class": "Taxonomy",
  "animal_order": "Taxonomy",
  "common_type": "Taxonomy",
  "common_sub_type": "Taxonomy",

  // Ecology & Habitat
  "range": "Ecology",
  "location": "Ecology",
  "host_plant": "Ecology",
  "description": "Ecology",

  // Rankings & Status
  "global_state_rank": "Rankings",
  "state_listing_status": "Rankings",
  "other_listing_status": "Rankings",
  "where_listed": "Rankings",
  "federal_rank": "Rankings",
  "california_rank": "Rankings",
  "cdfw_ranking": "Rankings",
  "forest_service": "Rankings",
  "iucn_rank": "Rankings",

  // BLM Data
  "blm_rank": "BLM Data",
  "blm_status": "BLM Data",

  // Notes
  "note": "Notes",
  "cdfw_notes": "Notes",
  "chez_note": "Notes",
  "taxonomy_notes": "Notes",
};

// Fields that should appear in multiple categories for animals
const ANIMAL_MULTI_CATEGORY_FIELDS = {
  "cdfw_ranking": ["Rankings", "CDFW Data"],
  "cdfw_notes": ["Notes", "CDFW Data"],
};

// With the flag on, the Jepson-sourced columns return to their own group.
if (JEPSON_DATA_ENABLED) {
  Object.assign(PLANT_CATEGORY_MAPPING, {
    jepson_description: "Jepson eFlora",
    jepson_eflora_other_notes: "Jepson eFlora",
    bioregional_distributions: "Jepson eFlora",
  });
}

const PLANT_CATEGORIES = [
  { name: "Main Data", className: "pill-main-data" },
  { name: "Ecology", className: "pill-ecology" },
  // Restored only with the flag (see src/utils/jepsonVisibility.ts).
  ...(JEPSON_DATA_ENABLED
    ? [{ name: "Jepson eFlora", className: "pill-jepson" }]
    : []),
  { name: "Rankings", className: "pill-rankings" },
  { name: "CNPS Data", className: "pill-cnps" },
  { name: "CAL IPC", className: "pill-cal-ipc" },
  { name: "CDFW Data", className: "pill-cdfw" },
  { name: "BLM Data", className: "pill-blm" },
  { name: "Wetland", className: "pill-wetland" },
];

const ANIMAL_CATEGORIES = [
  { name: "Taxonomy", className: "pill-main-data" },
  { name: "Ecology", className: "pill-ecology" },
  { name: "Rankings", className: "pill-rankings" },
  { name: "CDFW Data", className: "pill-cdfw" },
  { name: "BLM Data", className: "pill-blm" },
  { name: "Notes", className: "pill-notes" },
];

const CategoryDropdowns = ({ onChange, columns, type = "plant" }: Props) => {
  // Select the appropriate categories and mapping based on type
  const CATEGORIES = type === "animal" ? ANIMAL_CATEGORIES : PLANT_CATEGORIES;
  const CATEGORY_MAPPING = type === "animal" ? ANIMAL_CATEGORY_MAPPING : PLANT_CATEGORY_MAPPING;
  const [selectedColumns, setSelectedColumns] = useState<number[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    // Initialize with currently shown columns
    const initialSelected = columns
      .map((col, index) => ({ index, show: col.show }))
      .filter(item => item.show)
      .map(item => item.index);
    setSelectedColumns(initialSelected);
  }, [columns]);

  if (!columns.length) {
    return null;
  }

  // Group columns by category
  const columnsByCategory = CATEGORIES.reduce((acc, category) => {
    acc[category.name] = columns
      .map((col, index) => ({ ...col, index }))
      .filter((col) => {
        const dataIndex = col.dataIndex?.toLowerCase();
        const key = col.key?.toLowerCase();

        if (col.title === "Actions") return false;

        // Check standard mapping
        const inStandardMapping = CATEGORY_MAPPING[dataIndex] === category.name ||
                                  CATEGORY_MAPPING[key] === category.name;

        // Check multi-category fields (animals only)
        const inMultiCategory = type === "animal" && (
          (ANIMAL_MULTI_CATEGORY_FIELDS[dataIndex]?.includes(category.name)) ||
          (ANIMAL_MULTI_CATEGORY_FIELDS[key]?.includes(category.name))
        );

        return inStandardMapping || inMultiCategory;
      });
    return acc;
  }, {} as Record<string, any[]>);

  const handleCheckboxChange = (columnIndex: number, checked: boolean) => {
    let newSelection;
    if (checked) {
      newSelection = [...selectedColumns, columnIndex];
    } else {
      newSelection = selectedColumns.filter(idx => idx !== columnIndex);
    }

    setSelectedColumns(newSelection);

    const formattedSelection = newSelection.map(idx => ({
      value: idx,
      label: columns[idx].title
    }));

    onChange(formattedSelection);
  };

  const getCategoryContent = (category: string) => {
    const categoryColumns = columnsByCategory[category];

    if (!categoryColumns || categoryColumns.length === 0) {
      return <div>No fields available in this category</div>;
    }

    return (
      <div className="category-content">
        <div className="category-header">{category}</div>
        {categoryColumns.map((col) => (
          <div key={`checkbox-${col.index}`} className="checkbox-item">
            <Checkbox
              checked={selectedColumns.includes(col.index)}
              onChange={(e) => handleCheckboxChange(col.index, e.target.checked)}
            >
              {col.title}
            </Checkbox>
          </div>
        ))}
      </div>
    );
  };

  return (
    <CategoryDropdownsWrapper>
      <div className="category-pills-container">
        {CATEGORIES.map((category) => {
          const categoryColumns = columnsByCategory[category.name];
          const hasColumns = categoryColumns && categoryColumns.length > 0;

          if (!hasColumns) return null;

          return (
            <Popover
              key={category.name}
              content={getCategoryContent(category.name)}
              title={null}
              trigger="click"
              placement="bottom"
              open={activeCategory === category.name}
              onOpenChange={(visible) => setActiveCategory(visible ? category.name : null)}
              overlayClassName="category-popover"
            >
              <Button className={`category-pill ${category.className}`}>
                {category.name}
              </Button>
            </Popover>
          );
        })}
      </div>
    </CategoryDropdownsWrapper>
  );
};

export default CategoryDropdowns;
